import Swal from 'sweetalert2';
import { formatearMoneda } from './moneda';

/**
 * Normaliza y construye la descripción del ítem para un anteojo según la regla de negocio:
 * - Lentes de contacto y anteojos de sol: sin "recetado".
 * - Demás tipos: Anteojo + tipo de anteojo + recetado.
 */
export function construirDescripcionItemAnteojo(tipoAnteojo = '') {
  const tipoLimpio = (tipoAnteojo || '').trim();
  const lower = tipoLimpio.toLowerCase();

  if (!tipoLimpio) {
    return 'Anteojo recetado';
  }

  // Lentes de Contacto: sin 'recetado'
  if (lower.includes('contacto')) {
    return 'Lentes de Contacto';
  }

  // Sol: sin 'recetado'
  if (lower === 'sol' || lower === 'anteojo de sol' || lower === 'anteojos de sol') {
    return 'Anteojos de Sol';
  }

  // Si ya empieza con 'anteojo'
  if (lower.startsWith('anteojo')) {
    return `${tipoLimpio} recetado`;
  }

  return `Anteojo ${tipoLimpio} recetado`;
}

/**
 * Normaliza la descripción del ítem para una venta.
 */
export function construirDescripcionItemVenta(venta = {}) {
  if (venta.descripcion && venta.descripcion.trim()) {
    return venta.descripcion.trim();
  }
  if (venta.nombre_armazon && venta.nombre_armazon.trim()) {
    return `Armazón ${venta.nombre_armazon.trim()}`;
  }
  if (venta.armazon_marca) {
    const modelo = venta.armazon_modelo ? ` ${venta.armazon_modelo}` : '';
    const color = venta.armazon_color ? ` - ${venta.armazon_color}` : '';
    return `Armazón ${venta.armazon_marca}${modelo}${color}`.trim();
  }
  if (venta.producto_descripcion && venta.producto_descripcion.trim()) {
    return venta.producto_descripcion.trim();
  }
  if (venta.tipo_venta_nombre && venta.tipo_venta_nombre.trim()) {
    return `Venta ${venta.tipo_venta_nombre.trim()}`;
  }
  return 'Venta de óptica';
}

/**
 * Mapea la forma de pago a las opciones de facturación.
 */
export function mapearCondicionPago(tipoPago = '') {
  const t = (tipoPago || '').toLowerCase().trim();
  if (t === 'efectivo') return 'Efectivo';
  if (t === 'tarjeta_credito' || t.includes('crédito') || t.includes('credito')) return 'Tarjeta de crédito';
  if (t === 'tarjeta_debito' || t.includes('débito') || t.includes('debito')) return 'Tarjeta de débito';
  if (t === 'transferencia') return 'Transferencia';
  return 'Efectivo';
}

const STORAGE_KEY = 'factura_prellenada';

export function guardarBorradorFactura(datos) {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(datos));
  } catch (err) {
    console.error('Error al guardar borrador de factura:', err);
  }
}

export function obtenerBorradorFactura() {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch (_e) {
    return null;
  }
}

export function limpiarBorradorFactura() {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch (_e) {
    // silencioso
  }
}

export function navegarAFacturacion(onViewChange, onCloseModal) {
  if (typeof onCloseModal === 'function') {
    try {
      onCloseModal();
    } catch (_e) {}
  }

  if (typeof onViewChange === 'function') {
    onViewChange('facturacion');
  } else {
    try {
      localStorage.setItem('currentView', 'facturacion');
      globalThis.dispatchEvent(new CustomEvent('app-view-change', { detail: { view: 'facturacion' } }));
    } catch (_e) {
      // fallback
    }
  }
}

/**
 * Abre el Swal de confirmación para facturar un anteojo y redirige a Facturación.
 */
export async function solicitarFacturacionAnteojo({ cliente, anteojo, onViewChange, onCloseModal }) {
  const nombreCliente = `${cliente?.nombre || ''} ${cliente?.apellido || ''}`.trim() || 'Cliente';
  const descripcion = construirDescripcionItemAnteojo(anteojo?.tipo_anteojo);
  const monto = Number(anteojo?.precio_total || 0);
  const condicionPago = mapearCondicionPago(anteojo?.tipo_pago);

  const confirmacion = await Swal.fire({
    title: `¿Generar factura para ${nombreCliente}?`,
    html: `
      <div style="text-align: left; font-size: 14px; color: #374151; line-height: 1.6; padding: 4px 8px;">
        <p style="margin: 4px 0;"><strong>Cliente:</strong> ${nombreCliente}</p>
        <p style="margin: 4px 0;"><strong>Concepto:</strong> ${descripcion}</p>
        <p style="margin: 4px 0;"><strong>Forma de pago:</strong> ${condicionPago}</p>
        <p style="margin: 8px 0 0 0; font-size: 15px;"><strong>Monto:</strong> <span style="color: #059669; font-weight: 700;">${formatearMoneda(monto)}</span></p>
      </div>
    `,
    icon: 'question',
    showCancelButton: true,
    confirmButtonText: 'Sí, ir a Facturación',
    cancelButtonText: 'Cancelar',
    confirmButtonColor: '#2563eb',
    cancelButtonColor: '#64748b',
    reverseButtons: true,
  });

  if (confirmacion.isConfirmed) {
    guardarBorradorFactura({
      cliente_id: cliente?.id || null,
      anteojo_id: anteojo?.id || null,
      cliente: {
        nombre_razon_social: nombreCliente,
        cuit: '',
        condicion_iva: 'CF',
        email: cliente?.email || '',
        domicilio: cliente?.domicilio || '',
      },
      items: [
        {
          descripcion,
          cantidad: 1,
          precio_unitario: monto,
          alicuota_iva: 21,
        },
      ],
      condiciones_pago: condicionPago,
      observaciones: anteojo?.nro_sobre ? `Sobre N° ${anteojo.nro_sobre}` : (anteojo?.id ? `Anteojo #${anteojo.id}` : ''),
    });

    navegarAFacturacion(onViewChange, onCloseModal);
  }
}

/**
 * Abre el Swal de confirmación para facturar una venta y redirige a Facturación.
 */
export async function solicitarFacturacionVenta({ cliente, venta, onViewChange, onCloseModal }) {
  const nombreCliente = (cliente?.nombre || cliente?.apellido)
    ? `${cliente?.nombre || ''} ${cliente?.apellido || ''}`.trim()
    : `${venta?.cliente_nombre || ''} ${venta?.cliente_apellido || ''}`.trim() || 'Cliente';

  const descripcion = construirDescripcionItemVenta(venta);
  const monto = Number(venta?.monto || 0);
  const condicionPago = mapearCondicionPago(venta?.tipo_pago);

  const confirmacion = await Swal.fire({
    title: `¿Generar factura para ${nombreCliente}?`,
    html: `
      <div style="text-align: left; font-size: 14px; color: #374151; line-height: 1.6; padding: 4px 8px;">
        <p style="margin: 4px 0;"><strong>Cliente:</strong> ${nombreCliente}</p>
        <p style="margin: 4px 0;"><strong>Concepto:</strong> ${descripcion}</p>
        <p style="margin: 4px 0;"><strong>Forma de pago:</strong> ${condicionPago}</p>
        <p style="margin: 8px 0 0 0; font-size: 15px;"><strong>Monto:</strong> <span style="color: #059669; font-weight: 700;">${formatearMoneda(monto)}</span></p>
      </div>
    `,
    icon: 'question',
    showCancelButton: true,
    confirmButtonText: 'Sí, ir a Facturación',
    cancelButtonText: 'Cancelar',
    confirmButtonColor: '#2563eb',
    cancelButtonColor: '#64748b',
    reverseButtons: true,
  });

  if (confirmacion.isConfirmed) {
    guardarBorradorFactura({
      cliente_id: cliente?.id || null,
      cliente: {
        nombre_razon_social: nombreCliente,
        cuit: '',
        condicion_iva: 'CF',
        email: cliente?.email || '',
        domicilio: cliente?.domicilio || '',
      },
      items: [
        {
          descripcion,
          cantidad: 1,
          precio_unitario: monto,
          alicuota_iva: 21,
        },
      ],
      condiciones_pago: condicionPago,
      observaciones: venta?.id ? `Venta N° ${venta.id}` : '',
    });

    navegarAFacturacion(onViewChange, onCloseModal);
  }
}

/**
 * Busca si un anteojo ya tiene una factura emitida en el listado de facturas del cliente.
 */
export function encontrarFacturaDeAnteojo(anteojo, facturas = []) {
  if (!anteojo || !Array.isArray(facturas) || facturas.length === 0) return null;

  return facturas.find((f) => {
    // 1. Coincidencia directa por anteojo_id
    if (f.anteojo_id && Number(f.anteojo_id) === Number(anteojo.id)) return true;

    const obs = String(f.observaciones || '').toLowerCase();
    // 2. Coincidencia por número de sobre
    if (
      anteojo.nro_sobre &&
      (obs.includes(`sobre n° ${anteojo.nro_sobre}`) ||
        obs.includes(`sobre #${anteojo.nro_sobre}`) ||
        obs.includes(`sobre n°${anteojo.nro_sobre}`) ||
        obs.includes(`sobre ${anteojo.nro_sobre}`))
    ) {
      return true;
    }
    // 3. Coincidencia por ID de anteojo en observaciones
    if (
      anteojo.id &&
      (obs.includes(`anteojo #${anteojo.id}`) ||
        obs.includes(`anteojo n° ${anteojo.id}`) ||
        obs.includes(`anteojo ${anteojo.id}`))
    ) {
      return true;
    }
    return false;
  }) || null;
}

/**
 * Busca si una venta ya tiene una factura emitida en el listado de facturas del cliente.
 */
export function encontrarFacturaDeVenta(venta, facturas = []) {
  if (!venta || !Array.isArray(facturas) || facturas.length === 0) return null;

  return facturas.find((f) => {
    const obs = String(f.observaciones || '').toLowerCase();
    if (
      venta.id &&
      (obs.includes(`venta n° ${venta.id}`) ||
        obs.includes(`venta #${venta.id}`) ||
        obs.includes(`venta ${venta.id}`))
    ) {
      return true;
    }
    return false;
  }) || null;
}

/**
 * Abre un modal con los datos de la factura existente y la opción directa de descargar el PDF.
 */
export async function verODescargarFactura(factura, descargarFn) {
  if (!factura) return;

  const compLabel = factura.numero_comprobante || `${factura.tipo_factura} ${factura.punto_venta ? String(factura.punto_venta).padStart(4, '0') + '-' : ''}${String(factura.numero_factura).padStart(8, '0')}`;

  const result = await Swal.fire({
    title: `Factura ${factura.tipo_factura}`,
    html: `
      <div style="text-align: left; font-size: 14px; color: #374151; line-height: 1.6; padding: 6px 10px;">
        <p style="margin: 4px 0;"><strong>Comprobante:</strong> ${compLabel}</p>
        <p style="margin: 4px 0;"><strong>Cliente:</strong> ${factura.cliente_nombre || '-'}</p>
        <p style="margin: 4px 0;"><strong>Estado:</strong> <span style="font-weight: 600; color: ${factura.estado === 'AUTORIZADA' ? '#059669' : '#d97706'}">${factura.estado || 'Emitida'}</span></p>
        <p style="margin: 4px 0;"><strong>Total:</strong> <span style="font-weight: 700; color: #1f2937;">${formatearMoneda(factura.total)}</span></p>
        ${factura.cae ? `<p style="margin: 4px 0; font-size: 12px; color: #6b7280;"><strong>CAE:</strong> ${factura.cae}</p>` : ''}
      </div>
    `,
    icon: 'info',
    showCancelButton: true,
    confirmButtonText: 'Descargar PDF',
    cancelButtonText: 'Cerrar',
    confirmButtonColor: '#059669',
    cancelButtonColor: '#64748b',
    reverseButtons: true,
  });

  if (result.isConfirmed) {
    try {
      if (typeof descargarFn === 'function') {
        await descargarFn(factura.id, factura.numero_comprobante, factura.tipo_factura);
      }
    } catch (error) {
      console.error('Error al descargar factura PDF:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error al descargar',
        text: error.message || 'No se pudo descargar el archivo PDF de la factura.',
      });
    }
  }
}
